import { io, type Socket } from 'socket.io-client';
import { EVENTO_SESSAO_ENTREGA } from '@remoto/protocolo';
import { CAMINHO_WS } from '@remoto/shared';
import { garantirSessao } from '../api';
import { usarSessao } from '../estado/sessao';
import { aoFimDaEntrega, aoSocketReconectar } from '../sessoes/envios';

let socket: Socket | null = null;

/**
 * Os fluxos que esta aba quer ouvir, com um jeito de perguntar por onde ela já
 * está.
 *
 * O cursor é uma função, não um número: entre assinar e reassinar chegou
 * conversa, e reassinar com o cursor velho traria de volta tudo o que já está
 * na tela. Quem sabe o cursor de agora é quem mostra os eventos.
 */
const assinaturas = new Map<string, () => number>();

/** Ouvintes de reconexão: quem precisa repor o que perdeu enquanto esteve fora. */
const aoVoltar = new Set<() => void>();

let jaConectou = false;

function emitirAssinatura(alvo: Socket, fluxos: string[]): void {
  if (fluxos.length === 0) return;
  alvo.emit('assinar', {
    fluxos: fluxos.map((fluxo) => ({ fluxo, cursor: assinaturas.get(fluxo)?.() ?? 0 })),
  });
}

/**
 * Canal do painel, um por aba. O token é resolvido a cada tentativa de
 * conexão — inclusive nas reconexões — porque o access dura quinze minutos e
 * uma reconexão com token vencido seria recusada no handshake.
 */
export function socketDoPainel(): Socket {
  if (socket) return socket;
  const novo = io('/painel', {
    path: CAMINHO_WS,
    transports: ['websocket'],
    auth: (entregar) => {
      void garantirSessao().then(() => entregar({ token: usarSessao.getState().acesso ?? '' }));
    },
  });

  /**
   * Reconectar não é continuar: as salas do socket.io morrem com o socket, e o
   * servidor só refaz sozinho as do usuário e a de máquinas. A sala da sessão
   * é do cliente repor — sem isto, o tablet que dormiu volta com a conversa
   * muda até alguém recarregar a página, que é o defeito que este registro
   * existe para matar.
   */
  novo.on('connect', () => {
    emitirAssinatura(novo, [...assinaturas.keys()]);
    // Na primeira conexão não há nada a repor: quem acabou de carregar já
    // pediu tudo pelo REST.
    if (jaConectou) {
      // O que esperava o fim de uma entrega pode ter perdido o evento enquanto
      // o socket estava fora, e a API não o repete.
      aoSocketReconectar();
      for (const ouvinte of aoVoltar) ouvinte();
    }
    jaConectou = true;
  });

  /**
   * O fim de uma entrega de anexo em segundo plano: UM ouvinte, aqui, onde o
   * socket nasce — e não um por conversa aberta. A conversa a que o evento
   * pertence pode nem estar na tela (a pessoa foi olhar outra, ou mudou de
   * aba no celular), e é justamente quando uma falha precisa ficar guardada.
   * O evento vai à sala do usuário, que o servidor já refaz a cada conexão.
   */
  novo.on(EVENTO_SESSAO_ENTREGA, aoFimDaEntrega);

  /**
   * A conta entrou em outro aparelho (login único de não-ADMIN). O servidor
   * avisa e derruba o socket na sequência: registrar a saída AQUI é o que faz
   * a tela de entrar dizer o motivo em vez de parecer queda de rede.
   */
  novo.on('conta.derrubada', () => {
    usarSessao.getState().sair('substituida');
  });

  socket = novo;
  return novo;
}

/**
 * Assina um fluxo e mantém a assinatura viva através das quedas.
 *
 * Devolve a função de cancelar. Enquanto ela não for chamada, toda reconexão
 * reassina este fluxo com o cursor do momento.
 */
export function assinarFluxo(fluxo: string, cursor: () => number): () => void {
  const alvo = socketDoPainel();
  assinaturas.set(fluxo, cursor);
  if (alvo.connected) emitirAssinatura(alvo, [fluxo]);
  return () => {
    assinaturas.delete(fluxo);
    alvo.emit('desassinar', { fluxo });
  };
}

/** Registra quem quer recarregar o que perdeu quando o socket voltar. */
export function aoReconectar(ouvinte: () => void): () => void {
  aoVoltar.add(ouvinte);
  return () => {
    aoVoltar.delete(ouvinte);
  };
}

/**
 * A tela do aparelho acendendo é o sinal mais confiável de que o sono acabou.
 *
 * O socket.io reconecta sozinho na maioria das quedas, mas o aparelho que
 * dormiu horas pode voltar com as tentativas esgotadas — e aí ninguém tenta de
 * novo por conta própria.
 */
export function ligarDespertar(): () => void {
  const acordar = (): void => {
    if (document.visibilityState !== 'visible') return;
    const atual = socket;
    if (atual && !atual.connected) atual.connect();
  };
  document.addEventListener('visibilitychange', acordar);
  window.addEventListener('online', acordar);
  return () => {
    document.removeEventListener('visibilitychange', acordar);
    window.removeEventListener('online', acordar);
  };
}

export function fecharSocketDoPainel(): void {
  socket?.close();
  socket = null;
  assinaturas.clear();
  aoVoltar.clear();
  jaConectou = false;
}
